/**
 * @fileoverview useOnClickOutside - Click Outside Detection Hook
 *
 * Detects clicks outside a referenced element to trigger callbacks.
 * Essential for closing dropdowns, modals, and popover menus when
 * users click elsewhere on the page.
 *
 * @module hooks/useOnClickOutside
 */

import { useEffect } from 'react';

/**
 * Hook to detect clicks outside a referenced element.
 *
 * Attaches mousedown and touchstart event listeners to detect clicks
 * outside the provided ref element and calls the handler when detected.
 * Automatically cleans up listeners on unmount.
 *
 * @param {React.RefObject<HTMLElement>} ref - Reference to the element to monitor
 * @param {Function} handler - Callback function when click outside is detected
 *
 * @example
 * // Closing a dropdown menu
 * function Dropdown() {
 *   const [isOpen, setIsOpen] = useState(false);
 *   const dropdownRef = useRef<HTMLDivElement>(null);
 *
 *   useOnClickOutside(dropdownRef, () => {
 *     if (isOpen) setIsOpen(false);
 *   });
 *
 *   return (
 *     <div ref={dropdownRef}>
 *       <button onClick={() => setIsOpen(!isOpen)}>Toggle</button>
 *       {isOpen && (
 *         <ul className="dropdown-menu">
 *           <li>Option 1</li>
 *           <li>Option 2</li>
 *         </ul>
 *       )}
 *     </div>
 *   );
 * }
 *
 * @example
 * // Modal backdrop click handling
 * function Modal({ isOpen, onClose, children }) {
 *   const modalRef = useRef<HTMLDivElement>(null);
 *
 *   useOnClickOutside(modalRef, onClose);
 *
 *   if (!isOpen) return null;
 *
 *   return (
 *     <div className="modal-backdrop">
 *       <div ref={modalRef} className="modal-content">
 *         {children}
 *       </div>
 *     </div>
 *   );
 * }
 *
 * @example
 * // Search suggestions with outside click dismiss
 * function SearchWithSuggestions() {
 *   const [query, setQuery] = useState('');
 *   const [showSuggestions, setShowSuggestions] = useState(false);
 *   const containerRef = useRef<HTMLDivElement>(null);
 *
 *   useOnClickOutside(containerRef, () => setShowSuggestions(false));
 *
 *   return (
 *     <div ref={containerRef}>
 *       <input
 *         value={query}
 *         onChange={(e) => {
 *           setQuery(e.target.value);
 *           setShowSuggestions(true);
 *         }}
 *       />
 *       {showSuggestions && <SuggestionList query={query} />}
 *     </div>
 *   );
 * }
 */
export default function useOnClickOutside(ref: any, handler: any) {
  useEffect(() => {
    const listener = (event: any) => {
      if (!ref.current || ref.current.contains(event.target)) {
        return;
      }
      handler(event);
    };
    document.addEventListener('mousedown', listener);
    document.addEventListener('touchstart', listener);
    return () => {
      document.removeEventListener('mousedown', listener);
      document.removeEventListener('touchstart', listener);
    };
  }, [ref, handler]);
}
